import isEqual from "lodash/isEqual";

import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ClickToDialViewMode } from "@/Common/Components/CallCenter/ClickToDial.types";
import { ClickToDialView } from "@/Common/Components/CallCenter/ClickToDialView";
import { PharmacySearchAutocomplete } from "@/Common/Components/Controls/PharmacySearchAutocomplete";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { PreferredPharmacyHelper } from "@/ContactDirectory/Helpers/PreferredPharmacyHelper";
import { FileTypePrescription } from "@/ExportableFile/Models/FileTypes/FileTypePrescription";
import { IFaxInformationProps } from "./FaxAndSignatureLabRequestSelection";

import "./PharmacyFaxSelection.css";

class PharmacyFaxSelectionState {
    public customPharmacy: IPharmacyInfo = {} as IPharmacyInfo;
    public preferredPharmacy: IPharmacyInfo = {} as IPharmacyInfo;
    public isPreferred: boolean;
    public setPreferred: boolean;
    public isFaxValid: boolean;
    public isPhoneValid: boolean;

    constructor(s?: PharmacyFaxSelectionState) {
        Object.assign(this, s);
    }
}

class PharmacyFaxSelection extends BaseReactComponent<IFaxInformationProps<FileTypePrescription, IPharmacyInfo>, PharmacyFaxSelectionState> {
    private phoneValidationRule = new ValidationHelper().getPhoneNumberValidationRule(cacheStore.SiteCache[0].Model.Adresses[0].CountryLookupId, null);
    private validationRegex: string = this.phoneValidationRule ? this.phoneValidationRule.ValidationRegexPattern : "";
    private phoneMask: string = this.phoneValidationRule ? this.phoneValidationRule.EditMaskPattern : "";
    private preferredPharmacyHelper: PreferredPharmacyHelper;

    public constructor(props: IFaxInformationProps<FileTypePrescription, IPharmacyInfo>) {
        super(props);
        this.state = new PharmacyFaxSelectionState();
        this.preferredPharmacyHelper = new PreferredPharmacyHelper(
            patientStore.PatientModels[props.patientContactId]?.Addresses,
            sessionStore.getCurrentEmployee()
        );
    }

    public componentDidMount(): void {
        this.mutateState(
            s => {
                s.isPreferred = false;
                s.setPreferred = true;

                if (this.props.customPharmacy)
                    s.customPharmacy = this.props.customPharmacy;

                if (this.props.defaultContent) {
                    s.preferredPharmacy = this.props.defaultContent as IPharmacyInfo;
                    s.isFaxValid = this.getFaxNumberValidity(s.preferredPharmacy.Fax);
                    s.isPreferred = true;
                    s.setPreferred = false;

                    if (!IsNullOrWhiteSpace(this.props.customPharmacy?.Fax) && !IsNullOrWhiteSpace(this.getCustomPharmacyFaxNumber())) {
                        s.isPreferred = false;
                    }
                }
            },
            () => this.onPharmacyDataChange()
        );
    }

    public componentDidUpdate(prevProps: IFaxInformationProps<FileTypePrescription, IPharmacyInfo>): void {
        if (this.props.customPharmacy?.Fax != this.state.customPharmacy?.Fax && !isEqual(prevProps.customPharmacy, this.props.customPharmacy)) {
            this.mutateState(s => s.customPharmacy = this.props.customPharmacy);
        }
    }

    public render(): JSX.Element {
        const callCenterFeatureFlag: boolean = cacheStore.ClinicSettings.IsCallCenterEnabled();
        const formattedPhone: string = this.preferredPharmacyHelper.formatPhoneNumber(this.state.preferredPharmacy.Phone);
        let phoneElement: JSX.Element = <>{formattedPhone}</>;
        if (callCenterFeatureFlag) {
            phoneElement = <ClickToDialView viewMode={ClickToDialViewMode.Hyperlink} formattedPhoneNumber={formattedPhone} patientContactId={GuidHelper.AsGuid(this.props.patientContactId)} />;
        }
        return (
            <div className="pharmacy-fax-selection-container single-column centered">
                <div className="flex-display flex-col-gap-lg">
                    <div className="selecting-preferred-radio-button">
                        <input
                            type="radio"
                            name="pharmacy-option"
                            checked={this.state.isPreferred}
                            disabled={this.props.isSending}
                            onChange={() => this.setSelected(true)}
                        />
                    </div>

                    <div className="flex-grow-1">
                        {this.renderRow("PharmacyNameLabel", this.state.preferredPharmacy?.Banner, true)}
                        {this.renderRow("PharmacistsLabel", this.state.preferredPharmacy.Name, true)}
                        {this.renderRow("FaxLabel", this.preferredPharmacyHelper.formatPhoneNumber(this.state.preferredPharmacy.Fax), true)}
                        {this.renderRow("PhoneLabel", phoneElement, true)}
                        {this.renderRow("AddressLabel", this.state.preferredPharmacy.Address, true)}
                    </div>
                </div>

                <div className="flex-display flex-col-gap-lg" style={{ marginTop: "10px", borderTop: "1px solid #ccc", paddingTop: "20px", marginBottom: "5px" }}>

                    <div className="selecting-preferred-radio-button">
                        <input
                            type="radio"
                            name="pharmacy-option"
                            checked={!this.state.isPreferred}
                            disabled={this.props.isSending}
                            onChange={() => this.setSelected(false)}
                        />
                    </div>

                    <div className="flex-grow-1">
                        {this.renderRow(
                            "PharmacyNameLabel",
                            <PharmacySearchAutocomplete
                                componentId="custom_pharmacy"
                                onItemSelectCallback={item => this.onPharmacyInfoSelect(item)}
                                onFocusFunction={() => this.onFocusCustomPharmacy()}
                                onTextChange={value => this.editPharmacyBanner(value)}
                                valid={!IsNullOrWhiteSpace(this.state.customPharmacy.Banner) || this.state.isPreferred}
                                customPharmacyBanner={this.props.customPharmacy?.Banner}
                                customPharmacyFaxNumber={this.getCustomPharmacyFaxNumber()}
                            />
                        )}
                        {this.renderRow(
                            "PharmacistsLabel",
                            <TextboxControl
                                valid={!IsNullOrWhiteSpace(this.state.customPharmacy.Name) || this.state.isPreferred}
                                className="form-control"
                                disabled={this.props.isSending}
                                onChange={value => this.editPharmacists(value)}
                                onFocus={() => this.onFocusCustomPharmacy()}
                                value={this.state.customPharmacy.Name}
                                debounceMs={300}
                            />
                        )}
                        {this.renderRow(
                            "FaxLabel",
                            <TextboxMaskedControl
                                isValid={this.getFaxNumberValidity(this.state.customPharmacy.Fax) || this.state.isPreferred}
                                id="fax"
                                allowEmpty={false}
                                value={this.state.customPharmacy.Fax}
                                onChangeFunction={value => this.editFax(value)}
                                item={null}
                                mask={this.phoneMask}
                                validationErrorMessage=""
                                debounceMs={300}
                            />
                        )}
                        {this.renderRow(
                            "PhoneLabel",
                            <TextboxMaskedControl
                                isValid={this.state.isPhoneValid}
                                id="phone"
                                allowEmpty
                                value={this.state.customPharmacy.Phone}
                                onChangeFunction={value => this.editPhoneNumber(value)}
                                item={null}
                                mask={this.phoneMask}
                                validationErrorMessage=""
                                debounceMs={300}
                            />
                        )}
                        {this.renderRow(
                            "AddressLabel",
                            <TextboxControl
                                type="text"
                                className="form-control"
                                disabled={this.props.isSending}
                                onChange={val => this.mutateState(s => s.customPharmacy.Address = val)}
                                onFocus={() => this.onFocusCustomPharmacy()}
                                value={this.state.customPharmacy.Address}
                                debounceMs={300}
                            />
                        )}
                    </div>
                </div>

                <div className="selecting-preferred-radio-button checkbox">
                    <label>
                        <input
                            type="checkbox"
                            disabled={this.props.isSending || this.state.isPreferred}
                            checked={this.state.setPreferred}
                            onChange={() => this.mutateState(
                                s => {
                                    s.setPreferred = !s.setPreferred;
                                    s.isFaxValid = this.setFaxValidity(s.customPharmacy);
                                },
                                () => this.onPharmacyDataChange()
                            )}
                        />
                        {cacheStore.GetString("SetPreferedPharmacyLabel")}
                    </label>
                </div>
            </div>
        );
    }

    private renderRow(label: string, content: string | JSX.Element, applyStrongFrontWeight: boolean = false): JSX.Element {
        return (
            <div className="row-field">
                <div className="label-field">
                    <label className="form-label">{cacheStore.GetString(label)}</label>
                </div>
                <div className={`control-field ${applyStrongFrontWeight ? "font-weight-bold" : ""}`}>
                    {typeof content === "string" ? MyleHelper.ReplaceEmptyString(content)?.truncate(150) : content}
                </div>
            </div>
        );
    }

    private onPharmacyDataChange(): void {
        const exportableFile = this.props.exportableFile as FileTypePrescription;
        const pharmacy = {
            ...this.state.isPreferred
                ? this.state.preferredPharmacy
                : this.state.customPharmacy
        } as IPharmacyInfo;

        exportableFile?.trySetPharmarcyInfo(pharmacy, this.state.setPreferred);

        this.props.onDataChange(
            this.state.isFaxValid,
            cacheStore.GetString("PrescriptionLabel"),
            pharmacy.Fax,
            exportableFile, // without exportable file update,
            pharmacy.Phone
        );
    }

    private setSelected(isPreferred: boolean): void {
        this.mutateState(
            s => {
                s.isPreferred = isPreferred;
                s.setPreferred = !isPreferred;
                s.isFaxValid = s.isPreferred ? this.getFaxNumberValidity(s.preferredPharmacy.Fax) : this.setFaxValidity(s.customPharmacy);
                s.isPhoneValid = this.setPhoneValidity(s.isPreferred ? s.preferredPharmacy : s.customPharmacy);
            },
            () => {
                if (!this.state.isPreferred)
                    $("#custom_pharmacy input").focus();

                this.onPharmacyDataChange();
            }
        );
    }

    private onFocusCustomPharmacy(): void {
        if (this.state.isPreferred)
            this.mutateState(s => s.isPreferred = false);
    }

    private onPharmacyInfoSelect(item: IPharmacyInfo): void {
        if (item) {
            this.mutateState(
                s => {
                    s.customPharmacy = item;
                    s.isPreferred = false;
                    s.setPreferred = IsNullOrWhiteSpace(this.getCustomPharmacyFaxNumber());
                    s.isFaxValid = this.setFaxValidity(s.customPharmacy);
                    s.isPhoneValid = this.setPhoneValidity(s.customPharmacy);
                },
                () => this.onPharmacyDataChange()
            );
        }
    }

    private editPharmacyBanner(value: string): void {
        this.mutateState(
            s => {
                s.customPharmacy.Banner = value;
                s.isPreferred = false;
                s.isFaxValid = this.setFaxValidity(s.customPharmacy);
            },
            () => this.onPharmacyDataChange()
        );
    }

    private editPharmacists(value: string): void {
        this.mutateState(
            s => {
                s.customPharmacy.Name = value;
                s.isPreferred = false;
                s.isFaxValid = this.setFaxValidity(s.customPharmacy);
            },
            () => this.onPharmacyDataChange()
        );
    }

    private setFaxValidity(pharmacy: IPharmacyInfo): boolean {
        return this.getFaxNumberValidity(pharmacy.Fax) && !IsNullOrWhiteSpace(pharmacy?.Banner) && !IsNullOrWhiteSpace(pharmacy?.Name);
    }

    private getFaxNumberValidity(faxNumber: string): boolean {
        const formattedFaxNumber = this.getFormattedFaxNumber(faxNumber);
        const regex = new RegExp(this.validationRegex, "i");
        return !IsNullOrWhiteSpace(formattedFaxNumber) && regex.test(formattedFaxNumber.replace(/\D/g, ""));
    }

    private editFax(value: string): void {
        this.mutateState(
            s => {
                s.customPharmacy.Fax = value;
                s.isPreferred = false;
                s.isFaxValid = this.setFaxValidity(s.customPharmacy);
            },
            () => this.onPharmacyDataChange()
        );
    }

    private setPhoneValidity(pharmacy: IPharmacyInfo): boolean {
        const regex = new RegExp(this.validationRegex, "i");
        return IsNullOrWhiteSpace(pharmacy?.Phone) || regex.test(pharmacy.Phone.replace(/\D/g, ""));
    }

    private editPhoneNumber(value: string): void {
        this.mutateState(s => {
            s.customPharmacy.Phone = value;
            s.isPreferred = false;
            s.isPhoneValid = this.setPhoneValidity(s.customPharmacy);
            if (s.isPhoneValid)
                s.customPharmacy.Phone = value;
        });
    }

    private getFormattedFaxNumber(faxNumber: string = ""): string {
        return faxNumber.replace(/^(\+1)?|[-\s]/g, "");
    }

    private getCustomPharmacyFaxNumber(): string {
        const formattedFaxNumber = this.getFormattedFaxNumber(this.props.customPharmacy?.Fax);

        return !IsNullOrWhiteSpace(formattedFaxNumber) &&
            formattedFaxNumber != this.getFormattedFaxNumber(this.props.defaultContent?.Fax)
            ? formattedFaxNumber
            : "";
    }
}

export { PharmacyFaxSelection };
